/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetEmbedData';

const ActionSetEmbedDataManager: ActionManagerFn<'ActionSetEmbedData'> = (
  platform: Platform,
): ActionManager<'ActionSetEmbedData'> => {
  return {
    ...baseActionManager(platform, 'ActionSetEmbedData'),
    defaultValue,
    props: {
      ...baseActionProps,
      embeddedPageId: {
        label: 'Embedded Page ID',
        type: 'expression',
        helpText: 'select a page',
        returnType: async () => 'page',
      },
      key: {
        label: 'Key',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        helpText: (item: RequiredItem<'ActionSetEmbedData'>) =>
          `Once set, you can later access this value using the expression $.$pageData.${
            (typeof item.props.key === 'string'
              ? item.props.key
              : item.props.key.value) || '<key>'
          }`,
      },
      value: {
        label: 'Value',
        type: 'expression',
        returnType: async () => 'any',
        required: true,
      },
    },
    name: 'Set Embed Page Data',
    getDisplayText: (item) =>
      typeof item.props.key === 'string'
        ? item.props.key
        : item.props.key.value,
    description: `**Overview**<br/>
The Set Embed Page Data action facilitates the programmatic transfer of data from a parent page context into an embedded page. It acts as a bridge for state management, allowing builders to inject specific key-value pairs directly into an embedded page's local data scope ($pageData) for immediate use.

**Key Features**<br/>
* **Parent-to-Child Data Injection:** Designed to push data "down" from a container into a target embedded page, ensuring the child component has the necessary context to render or function correctly.
* **Dynamic Data Typing:** The Value property supports any type via expressions, allowing the transfer of strings, numbers, booleans, or complex objects/arrays without restriction.
* **Standardized Access Pattern:** Automatically structures data so it can be reliably retrieved within the embedded page using the standard $.$pageData.<key> expression syntax.
* **Expression Support:** Fully supports expressions for identifying the target page, defining the key, and calculating the value, enabling runtime flexibility.

**Properties**<br/>
* **Embedded Page ID:** An expression that resolves to the specific embedded page instance where the data will be stored.
* **Key (Required):** The unique identifier used to label the data.
  * *Constraint:* Must resolve to a string.
  * *Usage:* This defines the variable name for retrieval (e.g., if Key is 'userId', access it via $.$pageData.userId).
* **Value (Required):** The actual data payload to be stored.
  * *Constraint:* Accepts any data type.

**Use Cases**<br/>
* **Context Initialization:** Pre-loading an embedded form with default values or user specifics passed from the main dashboard.
* **Master-Detail Coordination:** Passing a selected item's ID from a parent list to an embedded detail view to trigger a specific data fetch.
* **Dynamic Configuration:** Injecting settings (such as display modes or feature flags) into a reusable embedded component to alter its behavior based on the parent context.`,
  };
};

export default ActionSetEmbedDataManager;
